"use client";
import React, { useMemo, useState, useEffect } from "react";
import { Box, CircularProgress, IconButton } from "@mui/material";
import { FiUnlock, FaLock, FiFileText } from "@/Components/Icons/Icons";
import ActionCellRendererExport from "@/Views/ExportJob/Dashboard/ActionCellRenderer";
import ActionCellRendererImport from "../../Views/ImportJob/Dashboard/ActionCellRenderer";
import { AgGridReact } from "@ag-grid-community/react";
import { ModuleRegistry } from "@ag-grid-community/core";
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-alpine.css";
import "@/App.css";
import TablePagination from "@/Components/Tables/Pagination";
import { useToggleLockMutation } from "../../redux/ExportPage/Dashboard/DashboardSlice";
import SuccessDialog from "@/Components/Dialog/SuccessDialog";
import { useNavigate } from "react-router-dom";

ModuleRegistry.registerModules([ClientSideRowModelModule]);

const iconStyle = { fontSize: "20px", color: "var(--brand-600)" };

function GridExample({
  columns,
  tableData,
  totalCount,
  currentPage,
  setCurrentPage,
  rowsPerPage,
  setRowsPerPage,
  handleRowClick,
  loading,
  type = "export",
  refetch, // ✅ added
}) {
  console.log("tableData in AgGridTable:", tableData);
  console.log("tableData in AgGridTable:", totalCount);
  const [page, setPage] = useState(0);
  // const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rowData, setRowData] = useState(tableData);
  const [toggleLock] = useToggleLockMutation();
  const navigate = useNavigate();

  // 🔹 dialog + action context
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successDialogConfig, setSuccessDialogConfig] = useState(null);
  const [successSource, setSuccessSource] = useState("");
  const [currentRowData, setCurrentRowData] = useState(null);
  const [highlightedRowId, setHighlightedRowId] = useState(null);
  const [loadingRowId, setLoadingRowId] = useState(null);
  const onCellClicked = (event) => {
    const stopColumns = ["actions", "exportLockStatus"];

    if (stopColumns.includes(event.colDef.field)) return;
    if (event.data?.exportLockStatus === "L" || event.data?.lockStatus === "L")
      return;
    // Only trigger row click for allowed columns
    if (handleRowClick) handleRowClick(event.data);
  };

  useEffect(() => {
    setRowData(tableData);
    const totalPages = Math.ceil((tableData?.length || 0) / rowsPerPage);
    setPage(0); // keep same behavior from your code
  }, [tableData, rowsPerPage]);

  const handleDeleteRow = (id) => {
    setRowData((prev) => prev.filter((row) => row.exporterMasterId !== id));
  };

  const handleLockChange = (id, newStatus) => {
    setRowData((prev) =>
      prev.map((row) =>
        row.exporterMasterId === id
          ? { ...row, exportLockStatus: newStatus }
          : row
      )
    );
  };

  const handlePrint = (rowData) => {
    const printWindow = window.open("", "_blank", "width=800,height=600");
    if (!printWindow) return;

    // exclude exporterMasterId from print
    const filteredData = Object.fromEntries(
      Object.entries(rowData).filter(([key]) => key !== "exporterMasterId")
    );

    const headerMap = {};
    columns.forEach((col) => {
      if (col.field) headerMap[col.field] = col.headerName || col.field;
    });

    printWindow.document.write(`
      <html>
        <head>
          <title>Print Job - ${rowData.exporterJobNumber}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h2 { color: #333; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
            th { background-color: #f4f4f4; }
          </style>
        </head>
        <body>
          <h2>Job Details</h2>
          <table>
            ${Object.entries(filteredData)
              .map(
                ([key, value]) => `
                  <tr>
                    <th>${headerMap[key] || key}</th>
                    <td>${value ?? ""}</td>
                  </tr>`
              )
              .join("")}
          </table>
          <script>
            window.print();
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };
  const handleRefresh = async (row) => {
    const rowId =
      type === "export"
        ? row.exporterMasterId
        : type === "import"
          ? row.jobId
          : type === "organization"
            ? row.customerHistoryId
            : row.id;
    setLoadingRowId(rowId);
    try {
      await new Promise((resolve) => setTimeout(resolve, 2000));

      setRowData((prev) =>
        prev.map((r) => {
          const currentRowId = type === "export" ? r.exporterMasterId : r.jobId;
          return currentRowId === rowId
            ? { ...r, refreshedAt: new Date().toLocaleTimeString() }
            : r;
        })
      );
    } finally {
      setLoadingRowId(null);
    }
  };

  const handleEdit = (row) => console.log("Edit clicked", row);
  const handleExportToExcel = (row) => console.log("Export to Excel", row);

  const getTextAlign = (align = "left") => {
    if (align === "center") return "center";
    if (align === "right" || align === "end") return "right";
    return "left";
  };

  const getFlexJustify = (align = "left") => {
    if (align === "center") return "center";
    if (align === "right" || align === "end") return "flex-end";
    return "flex-start";
  };

  const formatValue = (value) =>
    value === null || value === undefined || value === "" ? "-" : value;

  const columnDefs = useMemo(() => {
    return columns.map((col) => {
      const textAlign = getTextAlign(col.align);
      const justifyContent = col.justifyContent || getFlexJustify(col.align);
      const alignItems = col.alignItems || "center";
      const headerAlignClass = `header-align-${textAlign}`;

      const baseColDef = {
        ...col,
        pinned: col.pinned || undefined,
        minWidth: col.minWidth || 100,
        maxWidth: col.maxWidth,
        width: col.width || undefined,
        suppressMenu: false,
        suppressHeaderMenuButton: false,
        sortable: col.sort === true,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start", // ✅ always left for all rows
          textAlign: "left",
        },

        headerClass: headerAlignClass,
        lockPosition: col.pinned ? true : false,
        lockVisible: col.pinned ? true : false,
      };

      let OriginalRenderer;

      // 🔐 Lock/Unlock icon for import/export dynamically
      if (col.field === "exportLockStatus" || col.field === "lockStatus") {
        OriginalRenderer = (params) => {
          const statusField =
            params.data.exportLockStatus !== undefined
              ? "exportLockStatus"
              : "lockStatus";

          const isLocked =
            String(params.data[statusField])?.trim().toUpperCase() === "L";

          return (
            <IconButton size="small" sx={{ cursor: "default" }}>
              {isLocked ? (
                <FaLock style={{ ...iconStyle, borderRadius: "4px" }} />
              ) : (
                <FiUnlock style={iconStyle} />
              )}
            </IconButton>
          );
        };

        // 📄 E-Docket icon
      } else if (col.field === "edocket") {
        OriginalRenderer = (params) => (
          <IconButton size="small">
            <FiFileText style={iconStyle} />
          </IconButton>
        );

        // ⚙️ Action buttons — dynamic renderer (import/export)
      } else if (col.field === "actions") {
        OriginalRenderer = (params) => {
          const Renderer =
            type === "import"
              ? ActionCellRendererImport
              : ActionCellRendererExport;

          return (
            <Renderer
              {...params}
              onEdit={handleEdit}
              onRefresh={handleRefresh}
              onDelete={handleDeleteRow}
              onPrint={(rowData) => handlePrint(rowData)}
              onExportToExcel={handleExportToExcel}
              successDialogConfig={successDialogConfig}
              setShowSuccessDialog={setShowSuccessDialog}
              showSuccessDialog={showSuccessDialog}
              setSuccessDialogConfig={setSuccessDialogConfig}
              setCurrentRowData={setCurrentRowData}
              setSuccessSource={setSuccessSource}
              handleRowClick={handleRowClick}
              refetch={refetch} // ✅ pass it down
            />
          );
        };

        // 🧩 Custom icon column
      } else if (col.Icon) {
        OriginalRenderer = () => (
          <IconButton
            size="small"
            aria-label={col.headerName}
            title={col.headerName}
          >
            <col.Icon style={iconStyle} />
          </IconButton>
        );
      } else if (col.color) {
        OriginalRenderer = (params) => (
          <span style={{ color: col.color }}>{formatValue(params.value)}</span>
        );
      } else if (col.field === "jobStatus") {
        OriginalRenderer = (params) => {
          const status = formatValue(params.value).toLowerCase();
          let bgColor = "#9ca3af";
          let textColor = "var(--gray-0)";
          if (status === "sb failed" || status === "l.e.o") {
            bgColor = "var(--brand-100)";
            textColor = "var(--brand-600)";
          } else if (status === "job created") {
            bgColor = "var(--purple-50)";
            textColor = "var(--purple-800)";
          }
          return (
            <span
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: bgColor,
                color: textColor,
                padding: "4px 6px",
                borderRadius: "6px",
                height: "24px",
              }}
            >
              {formatValue(params.value)}
            </span>
          );
        };
      } else {
        OriginalRenderer = (params) => <div>{formatValue(params.value)}</div>;
      }

      baseColDef.cellRenderer = (params) => (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems,
            justifyContent,
          }}
          onClick={(e) => {
            if (col.stopRowClick) e.stopPropagation();
          }}
        >
          <OriginalRenderer {...params} />
        </div>
      );

      return baseColDef;
    });
  }, [columns, rowData]);

  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return rowData?.slice(start, end);
  }, [page, rowsPerPage, rowData]);

  return (
    <Box
      className="example-wrapper"
      sx={{ borderRadius: "10px", height: "628px" }}
    >
      <Box
        className="ag-theme-alpine"
        sx={{
          width: "100%",
          height: "620px",
          fontFamily: "Inter, sans-serif",
          "& .ag-pinned-left-cols-container, & .ag-pinned-right-cols-container":
            {
              overflowX: "hidden !important",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            },
          "& .ag-body-horizontal-scroll": {
            overflowX: "auto !important",
            "&::-webkit-scrollbar": { height: "12px" },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "var(--gray-100)",
              borderRadius: "60px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "var(--brand-100)",
              borderRadius: "60px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "var(--brand-300)",
            },
            scrollbarColor: "var(--gray-400) var(--gray-100)",
            scrollbarWidth: "thin",
          },
          "& .ag-header": {
            backgroundColor: "var(--brand-300)",
            fontWeight: 100,
          },
          "& .ag-header-cell-label": {
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start !important", // ✅ Force left align
            gap: "6px",
            fontWeight: 400,
            fontSize: "15px",
          },
          "& .ag-cell": {
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start", // ✅ force left for cells
            textAlign: "left",
          },

          // "& .header-align-left .ag-header-cell-label": {
          //   justifyContent: "flex-start",
          //   alignItems: "center",
          //   display: "flex",
          // },
          // "& .header-align-center .ag-header-cell-label": {
          //   justifyContent: "center",
          //   alignItems: "center",
          //   display: "flex",
          // },
          // "& .header-align-right .ag-header-cell-label": {
          //   justifyContent: "flex-end",
          //   alignItems: "center",
          //   display: "flex",
          // },
          // "& .ag-header-cell-label": {
          //   display: "flex",
          //   alignItems: "center",
          //   justifyContent: "space-between",
          // },
          "& .ag-header-cell-sortable .ag-header-icon": {
            opacity: 1,
            visibility: "visible",
          },
        }}
      >
        {loading ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "200px",
              width: "100%",
              gap: 1,
            }}
          >
            <CircularProgress size={40} sx={{ color: "var(--brand-600)" }} />
          </Box>
        ) : rowData?.length === 0 ? (
          // 2. Show "No Data Available"
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "200px",
              width: "100%",
            }}
          >
            <span style={{ color: "var(--gray-600)", fontSize: "16px" }}>
              No Data Available
            </span>
          </Box>
        ) : (
          <AgGridReact
            rowData={paginatedData}
            columnDefs={columnDefs}
            defaultColDef={{
              resizable: true,
              sortable: true,
              suppressMenu: false,
              headerClass: "custom-header",
              flex: 1,
            }}
            getRowId={(params) => {
              if (type === "export") return params.data.exporterMasterId;
              if (type === "import") return params.data.jobId;
              if (type === "organization") return params.data.customerId; // OR your correct id
              return params.data.id; // fallback
            }}
            pagination={false}
            rowHeight={50}
            headerHeight={60}
            domLayout="normal"
            isRowSelectable={(node) => node.data && !node.data.lock}
            rowClassRules={{
              "row-locked": (params) =>
                params.data?.exportLockStatus === "L" ||
                params.data?.lockStatus === "L",

              "row-loading": (params) =>
                params.data?.exporterMasterId === loadingRowId ||
                params.data?.jobId === loadingRowId,
            }}
            overlayNoRowsTemplate="<span>No Data Available</span>"
            suppressRowTransform={true}
            onCellClicked={onCellClicked}
            getRowStyle={(params) => {
              if (
                params.data?.exportLockStatus === "L" ||
                params.data?.lockStatus === "L"
              ) {
                return { cursor: "default" };
              } /*  */
              if (
                params.data?.exportLockStatus === "U" ||
                params.data?.lockStatus === "U"
              ) {
                return { cursor: "pointer" };
              }
              return { cursor: "default" };
            }}
          />
        )}
      </Box>

      <TablePagination
        component="div"
        count={totalCount}
        page={currentPage - 1}
        rowsPerPage={rowsPerPage}
        onPageChange={(_, newPage) => setCurrentPage(newPage + 1)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setCurrentPage(1);
        }}
        rowsPerPageOptions={[10, 20, 50, 100]}
      />

      <SuccessDialog
        open={showSuccessDialog}
        onClose={() => {
          setShowSuccessDialog(false);

          if (successSource === "delete" && currentRowData) {
            handleDeleteRow(currentRowData.exporterMasterId);
          }

          if (successSource === "lock" && currentRowData) {
            handleLockChange(
              currentRowData.exporterMasterId,
              currentRowData.newStatus
            );
          }

          setSuccessSource("");
          setCurrentRowData(null);
        }}
        {...successDialogConfig}
      />
      <SuccessDialog
        open={showSuccessDialog}
        onClose={() => {
          setShowSuccessDialog(false);
        }}
        {...successDialogConfig}
      />
    </Box>
  );
}

export default GridExample;
